import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Warning, Power, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const POLL_INTERVAL_MS = 40000;
const SPOT_INTERVAL_MS = 40000;
const MAX_TRAVAS_VISIBLE = 20;

const COLOR = {
  accent: "#40B6A2",
  danger: "#FF3B30",
  warning: "#FFB800",
  muted: "#A1A1AA",
  green: "#00FF66",
};

const calcMargemBrl = (spreadAplicado, volumeUsd) => (spreadAplicado ?? 0) * volumeUsd;

const fmt = (n, dec = 2) =>
  n?.toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtPrazoTrava = (t) => {
  if (t.tipo_vencimento === "ddf" && t.ddf_mes_embarque) {
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    const [ano, mes] = t.ddf_mes_embarque.split("-").map(Number);
    const abbr = cap(new Date(ano, mes - 1, 1).toLocaleDateString("pt-BR", { month: "short" }).replace(".", ""));
    return `DDF · ${abbr}/${String(ano).slice(2)}`;
  }
  return `${t.prazo_dias}d`;
};

function useHedgeDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [travas, setTravas] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spot, setSpot] = useState(null);
  const [spreadMedio, setSpreadMedio] = useState(null);
  const [spotHora, setSpotHora] = useState(null);
  const [ptaxPendentes, setPtaxPendentes] = useState([]);
  const timerRef = useRef(null);
  const spotTimerRef = useRef(null);
  const knownIdsRef = useRef(null); // null = carga inicial ainda não concluída
  const audioCtxRef = useRef(null);

  // AudioContext só pode ser criado após gesto do usuário (política dos browsers)
  useEffect(() => {
    const initAudio = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
    };
    window.addEventListener("click", initAudio, { once: true });
    window.addEventListener("keydown", initAudio, { once: true });
    return () => {
      window.removeEventListener("click", initAudio);
      window.removeEventListener("keydown", initAudio);
    };
  }, []);

  const tocarPing = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx || !document.hasFocus()) return;
    const nota = (freq, delaySec, durSec, vol) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(vol, ctx.currentTime + delaySec);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delaySec + durSec);
      osc.start(ctx.currentTime + delaySec);
      osc.stop(ctx.currentTime + delaySec + durSec);
    };
    nota(880, 0, 0.12, 0.3);
    nota(1100, 0.1, 0.08, 0.25);
  }, []);

  const loadSpot = useCallback(async () => {
    try {
      const s = await api.get("/cotacao/spot");
      setSpot(s.data);
      setSpotHora(new Date().toLocaleTimeString("pt-BR"));
    } catch {}
  }, []);

  const load = useCallback(async () => {
    try {
      const [m, t, cancelamentos, alcadas, r, ptax] = await Promise.all([
        api.get("/hedge/dashboard").catch(() => ({ data: {} })),
        api.get("/travas").catch(() => ({ data: [] })),
        api.get("/travas", { params: { status: "solicitacao_cancelamento" } }).catch(() => ({ data: [] })),
        api.get("/travas", { params: { status: "pendente_aprovacao_alcada" } }).catch(() => ({ data: [] })),
        api.get("/relatorio/margem").catch(() => ({ data: {} })),
        api.get("/ptax").catch(() => ({ data: [] })),
      ]);

      const listaTravas = t.data;
      if (knownIdsRef.current === null) {
        knownIdsRef.current = new Set(listaTravas.map((tv) => tv.codigo));
      } else {
        const temNova = listaTravas.some((tv) => !knownIdsRef.current.has(tv.codigo));
        if (temNova) {
          tocarPing();
          listaTravas.forEach((tv) => knownIdsRef.current.add(tv.codigo));
        }
      }

      setMetrics({ ...m.data, volume_descoberto_usd: m.data?.exposicao_dia ?? 0 });
      setTravas(listaTravas);
      setPendentes([...cancelamentos.data, ...alcadas.data]);
      setSpreadMedio(m.data?.spread_ponderado ?? r.data?.totalizadores?.spread_medio);
      setPtaxPendentes(ptax.data);
    } catch {
      toast.error("Erro ao carregar dashboard");
    } finally {
      setLoading(false);
    }
  }, [tocarPing]);

  useEffect(() => {
    loadSpot();
    load();
    spotTimerRef.current = setInterval(loadSpot, SPOT_INTERVAL_MS);
    timerRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      clearInterval(spotTimerRef.current);
      clearInterval(timerRef.current);
    };
  }, [load, loadSpot]);

  return { metrics, travas, pendentes, loading, spot, spreadMedio, spotHora, ptaxPendentes, reload: load };
}

export default function Hedge() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { metrics, travas, pendentes, loading, spot, spreadMedio, spotHora, ptaxPendentes, reload } = useHedgeDashboard();

  const toggleChave = async (v) => {
    try {
      await api.put(`/hedge/chave-geral?ativa=${v}`);
      toast.success(`Travas ${v ? "ativadas" : "desativadas"}`);
      reload();
    } catch {
      toast.error("Erro ao alterar chave geral");
    }
  };

  const aprovar = async (codigo, tipo) => {
    const endpoint = tipo === "cancel" ? "aprovar-cancelamento" : "aprovar-alcada";
    try {
      await api.post(`/travas/${codigo}/${endpoint}`);
      toast.success("Aprovado");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail ?? "Erro ao aprovar");
    }
  };

  const rejeitar = async (codigo, tipo) => {
    const endpoint = tipo === "cancel" ? "rejeitar-cancelamento" : "rejeitar-alcada";
    try {
      await api.post(`/travas/${codigo}/${endpoint}`);
      toast.success("Rejeitado");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail ?? "Erro ao rejeitar");
    }
  };

  const [filtrarHoje, setFiltrarHoje] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);

  if (loading || !metrics) return <Layout><div className="p-8 text-[#6b89a8]">Carregando...</div></Layout>;

  const exposicaoDia = metrics?.volume_descoberto_usd ?? 0;
  const limiteGlobal = metrics?.limite_global_usd || 1;
  const pctLimite = limiteGlobal > 0 ? (exposicaoDia / limiteGlobal) * 100 : 0;
  const alertaLimite = pctLimite >= 90;

  const hojeStr = new Date().toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  const travasFiltradas = filtrarHoje
    ? travas.filter((t) => new Date(t.data_trava).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }) === hojeStr)
    : travas;
  const travasTruncadas = travasFiltradas.length > MAX_TRAVAS_VISIBLE;

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6 grid-bg">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase">Hedge</p>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard de Exposição</h1>
          </div>
          <div className="flex items-center gap-3 border border-[#123a5e] rounded-sm px-4 py-3 bg-[#0B2D4D]">
            <Power className={`w-5 h-5 ${metrics.chave_geral_ativa ? "text-[#40B6A2]" : "text-[#FF3B30]"}`} weight="fill" />
            <div>
              <p className="text-[10px] tracking-widest uppercase text-[#6b89a8]">Chave Geral</p>
              <p className="text-sm font-semibold">{metrics.chave_geral_ativa ? "ATIVA" : "DESATIVADA"}</p>
            </div>
            {user?.role !== "gestao" && (
              <Switch checked={metrics.chave_geral_ativa} onCheckedChange={toggleChave} data-testid="switch-chave-geral" />
            )}
          </div>
        </div>



        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <MetricCard label="Trava Mesa" value={`USD ${fmt(metrics.trava_mesa_dia?.total ?? 0)}`} accent={COLOR.accent} subtitle={`${metrics.trava_mesa_dia?.quantidade ?? 0} trava${(metrics.trava_mesa_dia?.quantidade ?? 0) !== 1 ? "s" : ""} hoje`} testid="metric-trava-mesa" />
          <MetricCard label="PTAX" value={`USD ${fmt(metrics.ptax_dia?.total ?? 0)}`} accent={COLOR.warning} subtitle={`${metrics.ptax_dia?.quantidade ?? 0} ${(metrics.ptax_dia?.quantidade ?? 0) !== 1 ? "operações" : "operação"} hoje`} testid="metric-ptax-dia" />
          <MetricCard label="Falta Lançar Sysfértil" value={(metrics.falta_lancar ?? metrics.travas_ativas).toString()} accent={COLOR.accent} subtitle="travas sem pedido vinculado" testid="metric-ativas" />
          <MetricCard label="Exposição do Dia" value={`${fmt(metrics.volume_descoberto_usd)}`} accent={metrics.volume_descoberto_usd < 0 ? COLOR.danger : metrics.volume_descoberto_usd > 0 ? COLOR.warning : COLOR.muted} subtitle="posição líquida do dia" testid="metric-descoberto" />
          <MetricCard label="USD/BRL Spot" value={spot ? `R$ ${spot.spot.toFixed(4)}` : "..."} accent={COLOR.accent} subtitle={spotHora ? `Dados atuais · ${spotHora}` : "tempo real"} testid="metric-spot" />
          <MetricCard label="Spread Aplicado" value={spreadMedio != null ? `R$ ${spreadMedio.toFixed(4)}` : "..."} accent={COLOR.warning} subtitle="média ponderada por volume" testid="metric-spread" />
          <MetricCard label="Limite Global" value={`USD ${fmt(metrics.limite_global_usd)}`} accent={COLOR.muted} subtitle={alertaLimite ? "⚠ limite crítico" : "operacional"} testid="metric-limite" />
        </div>

        {alertaLimite && (
          <div className="border border-[#FF3B30] bg-[#FF3B30]/10 rounded-sm p-4 flex items-center gap-3">
            <Warning weight="fill" className="w-5 h-5 text-[#FF3B30]" />
            <p className="text-sm">
              <span className="font-bold">ALERTA CRÍTICO:</span> Exposição atingiu {pctLimite.toFixed(1)}% do limite global. Considere acionar a chave geral.
            </p>
          </div>
        )}

        {pendentes.length > 0 && (
          <div className="border border-[#FFB800]/40 bg-[#FFB800]/5 rounded-sm p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Warning weight="fill" className="w-5 h-5 text-[#FFB800]" />
              <h2 className="text-lg font-semibold">Pendências ({pendentes.length})</h2>
            </div>
            <div className="space-y-2">
              {pendentes.map((p) => {
                const tipo = p.status === "solicitacao_cancelamento" ? "cancel" : "alcada";
                return (
                  <div key={p.codigo} data-testid={`pend-${p.codigo}`} className="flex items-center gap-3 border border-[#123a5e] bg-[#0B2D4D] rounded-sm p-3">
                    <div className="flex-1 grid grid-cols-5 gap-3 text-sm">
                      <span className="font-mono-data font-semibold text-[#40B6A2]">{p.codigo}</span>
                      <span className="truncate">{p.cliente}</span>
                      <span className="font-mono-data text-xs">USD {fmt(p.volume_usd)}</span>
                      <span className="text-xs text-[#8aa9c9]">{p.vendedor_nome}</span>
                      <span className="text-xs uppercase tracking-wider text-[#FFB800]">
                        {tipo === "cancel" ? "cancelamento" : "alçada"}
                      </span>
                    </div>
                    <Button size="sm" onClick={() => tipo === "cancel" ? setConfirmModal({ codigo: p.codigo, tipo, acao: "aprovar", pendencia: p }) : aprovar(p.codigo, tipo)} data-testid={`btn-aprovar-${p.codigo}`} className="h-8 bg-[#40B6A2] text-black hover:bg-[#359a89]">
                      <CheckCircle className="w-4 h-4 mr-1" /> Aprovar
                    </Button>
                    <Button size="sm" onClick={() => tipo === "cancel" ? setConfirmModal({ codigo: p.codigo, tipo, acao: "rejeitar", pendencia: p }) : rejeitar(p.codigo, tipo)} data-testid={`btn-rejeitar-${p.codigo}`} className="h-8 bg-[#FF3B30] text-white hover:bg-red-700">
                      <XCircle className="w-4 h-4 mr-1" /> Rejeitar
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="border border-[#123a5e] rounded-sm overflow-hidden">
          <div className="p-4 border-b border-[#123a5e] flex items-center justify-between bg-[#0B2D4D]">
            <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">Travas ativas · tempo real</h2>
            <div className="flex items-center gap-3">
              <button
                data-testid="btn-filtrar-hoje"
                onClick={() => setFiltrarHoje((v) => !v)}
                className={`text-[10px] tracking-[0.15em] uppercase px-3 py-1 border rounded-sm transition-colors ${
                  filtrarHoje
                    ? "border-[#40B6A2] text-[#40B6A2] bg-[#40B6A2]/10"
                    : "border-[#1a4773] text-[#6b89a8] hover:border-[#40B6A2]/50 hover:text-[#8aa9c9]"
                }`}
              >
                Hoje
              </button>
              <span className="text-xs text-[#6b89a8] font-mono-data">
                {travasTruncadas
                  ? `${MAX_TRAVAS_VISIBLE} de ${travasFiltradas.length} registros`
                  : `${travasFiltradas.length} registros`}
                {filtrarHoje && travas.length !== travasFiltradas.length && (
                  <span className="text-[#6b89a8]/60"> · {travas.length} total</span>
                )}
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#061729] border-b border-[#123a5e]">
                <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#6b89a8]">
                  <th className="p-3">Hora</th>
                  <th className="p-3">Código</th>
                  <th className="p-3">Vendedor</th>
                  <th className="p-3">Cliente</th>
                  <th className="p-3 text-right">Vol. USD</th>
                  <th className="p-3 text-right">Spot</th>
                  <th className="p-3 text-right">Taxa</th>
                  <th className="p-3 text-right">Margem BRL</th>
                  <th className="p-3">Prazo</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {travasFiltradas.slice(0, MAX_TRAVAS_VISIBLE).map((t) => {
                  const margemBrl = calcMargemBrl(t.spread_aplicado, t.volume_usd);
                  return (
                    <tr key={t.codigo} className="border-b border-[#0e2e4e] hover:bg-[#0B2D4D]/50">
                      <td className="p-3 font-mono-data text-xs text-[#8aa9c9]">{new Date(t.data_trava).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="p-3 font-mono-data font-semibold text-[#40B6A2]">{t.codigo}</td>
                      <td className="p-3 text-xs truncate max-w-[150px]">{t.vendedor_nome}</td>
                      <td className="p-3 truncate max-w-[180px]">{t.cliente}</td>
                      <td className="p-3 text-right font-mono-data">{fmt(t.volume_usd)}</td>
                      <td className="p-3 text-right font-mono-data text-[#8aa9c9]">R$ {fmt(t.spot, 4)}</td>
                      <td className="p-3 text-right font-mono-data">R$ {fmt(t.taxa_final, 4)}</td>
                      <td className="p-3 text-right font-mono-data" style={{ color: COLOR.green }}>R$ {fmt(margemBrl)}</td>
                      <td className="p-3 font-mono-data text-xs">{fmtPrazoTrava(t)}</td>
                      <td className="p-3"><StatusBadge status={t.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {ptaxPendentes.length > 0 && (
          <div className="border border-[#123a5e] rounded-sm overflow-hidden">
            <div className="p-4 border-b border-[#123a5e] flex items-center justify-between bg-[#0B2D4D]">
              <h2 className="text-sm tracking-[0.2em] uppercase text-[#FFB800]">PTAX Pendentes</h2>
              <span className="text-xs text-[#6b89a8] font-mono-data">{ptaxPendentes.length} registro{ptaxPendentes.length > 1 ? "s" : ""}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#061729] border-b border-[#123a5e]">
                  <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#6b89a8]">
                    <th className="p-3">Código</th>
                    <th className="p-3">Cliente</th>
                    <th className="p-3 text-right">Vol. USD</th>
                    <th className="p-3">Tipo</th>
                    <th className="p-3 text-right">Taxa PTAX</th>
                  </tr>
                </thead>
                <tbody>
                  {ptaxPendentes.map((p) => {
                    const fmtData = (s) => { if (!s) return "—"; const [a, m, d] = s.split("-"); return `${d}/${m}/${a}`; };
                    const tipoLabel = p.tipo_vencimento === "ddf"
                      ? (p.data_faturamento
                          ? `DDF · faturado ${new Date(p.data_faturamento).toLocaleDateString("pt-BR")}`
                          : `DDF · ${p.ddf_dias} dias`)
                      : `DATA FIXA · ${fmtData(p.data_vencimento)}`;
                    return (
                      <tr key={p.codigo} className="border-b border-[#0e2e4e] hover:bg-[#0B2D4D]/50">
                        <td className="p-3 font-mono-data font-semibold text-[#FFB800]">{p.codigo}</td>
                        <td className="p-3 truncate max-w-[200px]">{p.cliente}</td>
                        <td className="p-3 text-right font-mono-data">{fmt(p.volume_usd)}</td>
                        <td className="p-3 font-mono-data text-xs text-[#8aa9c9]">{tipoLabel}</td>
                        <td className="p-3 text-right font-mono-data">
                          {p.taxa_ptax ? `R$ ${fmt(p.taxa_ptax)}` : <span className="text-[#6b89a8] italic text-xs">Aguardando</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!confirmModal} onOpenChange={(open) => !open && setConfirmModal(null)}>
        <DialogContent className="bg-[#0B2D4D] border border-[#123a5e] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              {confirmModal?.acao === "aprovar"
                ? <><CheckCircle weight="fill" className="w-5 h-5 text-[#40B6A2]" /> Aprovar Cancelamento</>
                : <><XCircle weight="fill" className="w-5 h-5 text-[#FF3B30]" /> Rejeitar Cancelamento</>
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#8aa9c9]">Trava</span>
              <span className="font-mono-data text-white">{confirmModal?.pendencia?.codigo}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#8aa9c9]">Cliente</span>
              <span className="text-white">{confirmModal?.pendencia?.cliente}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#8aa9c9]">Vendedor</span>
              <span className="text-white">{confirmModal?.pendencia?.vendedor_nome}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#8aa9c9]">Volume</span>
              <span className="font-mono-data text-white">USD {fmt(confirmModal?.pendencia?.volume_usd)}</span>
            </div>
            {confirmModal?.pendencia?.motivo_cancelamento && (
              <div className="space-y-1">
                <span className="text-xs tracking-wider uppercase text-[#8aa9c9]">Motivo do vendedor</span>
                <p className="text-sm text-[#FFB800] leading-relaxed bg-[#0a2540] rounded-sm px-3 py-2 border border-[#123a5e]">
                  {confirmModal.pendencia.motivo_cancelamento}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirmModal(null)} className="border-[#123a5e] text-[#8aa9c9] hover:text-white">
              Voltar
            </Button>
            <Button
              onClick={() => {
                const { codigo, tipo, acao } = confirmModal;
                setConfirmModal(null);
                acao === "aprovar" ? aprovar(codigo, tipo) : rejeitar(codigo, tipo);
              }}
              className={confirmModal?.acao === "aprovar"
                ? "bg-[#40B6A2] text-black hover:bg-[#359a89]"
                : "bg-[#FF3B30] text-white hover:bg-red-700"
              }
            >
              {confirmModal?.acao === "aprovar" ? "Confirmar Aprovação" : "Confirmar Rejeição"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function MetricCard({ label, value, accent, subtitle, testid, children }) {
  return (
    <div className="border border-[#123a5e] rounded-sm p-5 bg-[#0B2D4D]" data-testid={testid}>
      <p className="text-[10px] tracking-[0.2em] uppercase text-[#6b89a8] mb-3">{label}</p>
      <p className="text-[clamp(0.65rem,2vw,1.5rem)] font-bold font-mono-data leading-tight" style={{ color: accent }}>{value}</p>
      <p className="text-xs text-[#6b89a8] mt-1">{subtitle}</p>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pendente_vinculacao: { label: "Pendente", color: "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30" },
    pendente_aprovacao_alcada: { label: "Alçada", color: "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30" },
    vinculada: { label: "Vinculada", color: "bg-[#40B6A2]/10 text-[#40B6A2] border-[#40B6A2]/30" },
    cancelada: { label: "Cancelada", color: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30" },
    rejeitada: { label: "Rejeitada", color: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30" },
    solicitacao_cancelamento: { label: "Canc. Pend.", color: "bg-orange-500/10 text-orange-500 border-orange-500/30" },
  };
  const c = map[status] || { label: status, color: "border-[#1a4773]" };
  return <Badge className={`${c.color} border font-mono-data text-[10px] tracking-wider uppercase`}>{c.label}</Badge>;
}
